import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Download, CreditCard, Calendar, Receipt } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
}

interface Invoice {
  id: string;
  number: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  paidAt?: number;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  periodStart?: number;
  periodEnd?: number;
}

interface SubscriptionDetails {
  hasSubscription: boolean;
  subscription?: {
    id: string;
    status: string;
    tier: string;
    amount: number;
    currency: string;
    interval: string;
    intervalCount: number;
    currentPeriodStart: number;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
    canceledAt?: number;
    created: number;
  };
  product?: {
    id: string;
    name: string;
    description?: string;
  };
  customer?: {
    id: string;
    email: string;
    name?: string;
    created: number;
  };
  paymentMethod?: PaymentMethod;
  invoices?: Invoice[];
  upcomingInvoice?: {
    amount: number;
    subtotal: number;
    total: number;
    currency: string;
    periodStart: number;
    periodEnd: number;
    nextPaymentAttempt?: number;
    discount?: {
      id: string;
      couponId: string;
      couponName: string;
      percentOff: number | null;
      amountOff: number | null;
      currency: string | null;
      valid: boolean;
      start: number;
      end: number | null;
    };
    totalDiscountAmount: number;
  };
  error?: string;
}

interface Props {
  onManageSubscription: () => void;
}

export const ProfessionalSubscriptionDetails: React.FC<Props> = ({ onManageSubscription }) => {
  const [details, setDetails] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.functions.invoke('get-subscription-details');
      
      if (error) {
        throw error;
      }

      setDetails(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscription details';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const handleDownloadInvoice = (invoiceUrl?: string) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !details) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error || 'Failed to load subscription details'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!details.hasSubscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Subscription Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No active subscription found.</p>
        </CardContent>
      </Card>
    );
  }

  const { subscription, product, customer, paymentMethod, invoices, upcomingInvoice } = details;

  return (
    <div className="space-y-6">
      {/* Subscription Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Subscription Overview
            </span>
            <Badge variant={subscription?.status === 'active' ? 'default' : 'secondary'}>
              {subscription?.status}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Plan</label>
              <p className="text-base font-semibold">{subscription?.tier} Plan</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Amount</label>
              <p className="text-base font-semibold">
                {formatCurrency(subscription?.amount || 0, subscription?.currency || 'usd')}
                /{subscription?.interval}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Current Period</label>
              <p className="text-base">
                {formatDate(subscription?.currentPeriodStart || 0)} - {formatDate(subscription?.currentPeriodEnd || 0)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Next Billing Date</label>
              <p className="text-base">
                {subscription?.cancelAtPeriodEnd 
                  ? 'Subscription ends ' + formatDate(subscription.currentPeriodEnd)
                  : formatDate(subscription?.currentPeriodEnd || 0)
                }
              </p>
            </div>
          </div>

          {subscription?.cancelAtPeriodEnd && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your subscription is set to cancel at the end of the current billing period.
              </AlertDescription>
            </Alert>
          )}

          <Separator />

          <div className="flex justify-end">
            <Button onClick={onManageSubscription} variant="outline">
              Manage Subscription
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payment Method */}
      {paymentMethod && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Method
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              {paymentMethod.card && (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium capitalize">
                      {paymentMethod.card.brand}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      •••• {paymentMethod.card.last4}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Expires {paymentMethod.card.expMonth}/{paymentMethod.card.expYear}
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Billing */}
      {upcomingInvoice && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Next Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Amount Due</label>
                  <p className="text-lg font-semibold">
                    {formatCurrency(upcomingInvoice.amount || 0, upcomingInvoice.currency)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Billing Period</label>
                  <p className="text-base">
                    {formatDate(upcomingInvoice.periodStart)} - {formatDate(upcomingInvoice.periodEnd)}
                  </p>
                </div>
              </div>

              {upcomingInvoice.discount && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-600">🎉 Coupon Applied</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Coupon:</span>
                      <span className="font-medium">
                        {upcomingInvoice.discount.couponName || upcomingInvoice.discount.couponId}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Discount:</span>
                      <span className="font-medium text-green-600">
                        {upcomingInvoice.discount.percentOff 
                          ? `${upcomingInvoice.discount.percentOff}% off`
                          : upcomingInvoice.discount.amountOff 
                            ? `${formatCurrency(upcomingInvoice.discount.amountOff, upcomingInvoice.discount.currency || upcomingInvoice.currency)} off`
                            : 'Applied'
                        }
                      </span>
                    </div>
                  </div>
                  {upcomingInvoice.totalDiscountAmount > 0 && (
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-sm text-muted-foreground">Total discount:</span>
                      <span className="font-medium text-green-600">
                        -{formatCurrency(upcomingInvoice.totalDiscountAmount, upcomingInvoice.currency)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {upcomingInvoice.amount === 0 && upcomingInvoice.discount && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your next billing amount is $0.00 due to the applied coupon. You won't be charged for this billing period.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing History */}
      {invoices && invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Billing History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">
                      {invoice.number}
                    </TableCell>
                    <TableCell>
                      {formatDate(invoice.created)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadInvoice(invoice.invoicePdf)}
                        disabled={!invoice.invoicePdf}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};